import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  App,
  Button,
  Card,
  Checkbox,
  Form,
  Input,
  Select,
  Space,
  Typography,
} from 'antd';
import type { RuleObject } from 'antd/es/form';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { vendorBucketPrefix } from '@/utils/config';
import { vendorApi } from '@/utils/vendorApi';
import { Authorization, SmbEndpointOption, VendorCreateRequest } from '@/types';
import { useAuthStore, businessUnitLabel } from '@/store/authStore';
import { canAssignVendorTag } from '@/store/vendorAccess';
import SourceRoutesEditor, {
  buildSourceEndpointOptions,
  defaultCreateRoute,
  normalizeSourceRoutes,
} from '@/components/vendor/SourceRoutesEditor';

const { Title, Text } = Typography;

const EMAIL_RULE = { type: 'email' as const, message: 'Enter a valid email address' };

const BUCKET_PREFIX = vendorBucketPrefix();
const SUFFIX_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

function normalizeBucketSuffix(raw: string): string {
  return raw.trim().toLowerCase().replace(/-+/g, '-').replace(/^-|-$/g, '');
}

function validateBucketSuffix(_rule: RuleObject, value: string) {
  const raw = (value || '').trim();
  if (!raw) {
    return Promise.reject(new Error('Enter the vendor suffix (e.g. alta).'));
  }
  if (/[^A-Za-z0-9-]/.test(raw)) {
    return Promise.reject(
      new Error('Vendor suffix may contain only letters, numbers, and hyphens (e.g. alta).')
    );
  }
  const suffix = normalizeBucketSuffix(raw);
  if (!suffix) {
    return Promise.reject(new Error('Enter the vendor suffix (e.g. alta).'));
  }
  if (suffix.startsWith('data-exchange-')) {
    return Promise.reject(
      new Error(
        `Enter only the vendor-specific name (e.g. alta). The '${BUCKET_PREFIX}' prefix is added automatically.`
      )
    );
  }
  if (!SUFFIX_PATTERN.test(suffix)) {
    return Promise.reject(
      new Error('Vendor suffix may contain only letters, numbers, and hyphens (e.g. alta).')
    );
  }
  return Promise.resolve();
}

type VendorCreateFormValues = VendorCreateRequest & {
  outbound_bucket_suffix: string;
};

function createBusinessUnitOptions(authorization: Authorization | null | undefined) {
  if (!authorization) {
    return [];
  }
  if (authorization.globalAdmin) {
    return (authorization.catalog ?? []).map((entry) => ({
      value: entry.id,
      label: businessUnitLabel(entry.id),
    }));
  }
  return authorization.units
    .filter((unit) => unit.actions.some((action) => action.trim() === 'vendors.create'))
    .map((unit) => ({ value: unit.id, label: businessUnitLabel(unit.id) }));
}

const VendorCreatePage: React.FC = () => {
  const { message } = App.useApp();
  const { authorization } = useAuthStore();
  const buOptions = createBusinessUnitOptions(authorization);
  const listPath = authorization?.globalAdmin ? '/vendors' : '/my-vendors';
  const navigate = useNavigate();
  const [form] = Form.useForm<VendorCreateFormValues>();
  const [submitting, setSubmitting] = useState(false);
  const [endpoints, setEndpoints] = useState<SmbEndpointOption[]>([]);
  const [endpointsLoading, setEndpointsLoading] = useState(true);
  const [endpointsUnavailable, setEndpointsUnavailable] = useState(false);
  const [tagChoices, setTagChoices] = useState<string[]>([]);
  const selectedBu = Form.useWatch('bu', form);
  const assignTag = canAssignVendorTag(authorization, selectedBu);
  const endpointOptions = buildSourceEndpointOptions(endpoints, ['dev-dxgprivate']);

  useEffect(() => {
    let cancelled = false;
    vendorApi.listEndpoints()
      .then((rows) => {
        if (!cancelled) {
          setEndpoints(rows);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setEndpointsUnavailable(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setEndpointsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!assignTag || !selectedBu) {
      setTagChoices([]);
      return;
    }
    let cancelled = false;
    vendorApi.listTags(selectedBu)
      .then((rows) => {
        if (!cancelled) {
          setTagChoices(rows);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTagChoices([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [assignTag, selectedBu]);

  const onFinish = async (values: VendorCreateFormValues) => {
    const hostname = (values.hostname || 'default').trim() || 'default';
    const body: VendorCreateRequest = {
      vendor_id: values.vendor_id.trim(),
      hostname,
      bu: values.bu,
      sftp_username: values.sftp_username.trim(),
      vendor_email: values.vendor_email.trim(),
      success_email: values.success_email.trim(),
      failure_email: values.failure_email.trim(),
      outbound_bucket_suffix: normalizeBucketSuffix(values.outbound_bucket_suffix),
      send_email_to_vendor: values.send_email_to_vendor ?? true,
    };
    const prefix = values.outbound_prefix?.trim();
    if (prefix) {
      body.outbound_prefix = prefix;
    }
    const sourceEndpointId = values.source_endpoint_id?.trim();
    if (sourceEndpointId) {
      body.source_endpoint_id = sourceEndpointId;
    }
    body.source_routes = normalizeSourceRoutes(values.source_routes);
    if (assignTag && values.tag?.trim()) {
      body.tag = values.tag.trim();
    }

    setSubmitting(true);
    try {
      const result = await vendorApi.createVendor(body);
      if (result.status === 'created' || result.status === 'regenerated') {
        message.success(result.message || `Vendor ${result.vendor_id} ${result.status}`);
        const detailBase = authorization?.globalAdmin ? '/vendors' : '/my-vendors';
        navigate(
          `${detailBase}/${encodeURIComponent(result.vendor_id)}?hostname=${encodeURIComponent(hostname)}`
        );
        return;
      }
      if (result.status === 'skipped') {
        message.warning(
          result.message ||
            'This vendor already exists. Open the vendor detail page and use Re-invite / Resend Onboarding to send a new link.'
        );
        return;
      }
      message.error(result.message || 'Vendor create failed');
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create vendor';
      message.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ padding: 24, width: '100%', maxWidth: 720, margin: '0 auto' }}>
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate(listPath)}
        style={{ marginBottom: 16 }}
      >
        Back to Vendors
      </Button>
      <Title level={2} style={{ marginTop: 0 }}>Create Vendor</Title>
      <Text type="secondary">
        Submitting provisions a real S3 bucket and can send a real onboarding email.
      </Text>
      <Alert
        type="warning"
        showIcon
        style={{ margin: '16px 0' }}
        message="Creates a live vendor configuration"
        description="Submitting this form provisions the vendor's S3 bucket and standard folders. If 'Send onboarding email' is selected, the vendor will receive a single-use link to complete SSH and PGP key setup."
      />
      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            hostname: 'default',
            send_email_to_vendor: true,
            source_routes: [defaultCreateRoute()],
          }}
        >
          <Form.Item
            name="vendor_id"
            label="Vendor ID"
            extra="Unique identifier for this vendor. Until you edit it, the first route's file pattern is {vendor id}_*."
            rules={[{ required: true, message: 'Vendor ID is required' }]}
          >
            <Input placeholder="vendor-0000001" />
          </Form.Item>
          <Form.Item
            name="hostname"
            label="Hostname"
            extra="Identifies this vendor's configuration record. Leave as 'default' unless this vendor needs multiple separate host configurations (rare)."
            rules={[{ required: true, message: 'Hostname is required' }]}
          >
            <Input placeholder="default" />
          </Form.Item>
          <Form.Item
            name="sftp_username"
            label="SFTP Username"
            extra="The vendor's SFTP login name."
            rules={[{ required: true, message: 'SFTP Username is required' }]}
          >
            <Input placeholder="vendor_acme" />
          </Form.Item>
          <Form.Item
            name="vendor_email"
            label="Vendor Email"
            extra="Receives the single-use key exchange link."
            rules={[{ required: true, message: 'Vendor Email is required' }, EMAIL_RULE]}
          >
            <Input placeholder="onboarding@vendor.com" />
          </Form.Item>
          <Form.Item
            name="success_email"
            label="Default Success Email"
            extra="Used for any route that doesn't have its own list. A route with its own list sends only to that list."
            rules={[{ required: true, message: 'Success Email is required' }, EMAIL_RULE]}
          >
            <Input placeholder="success@vendor.com" />
          </Form.Item>
          <Form.Item
            name="failure_email"
            label="Default Failure Email"
            extra="Used for any route that doesn't have its own list. A route with its own list sends only to that list."
            rules={[{ required: true, message: 'Failure Email is required' }, EMAIL_RULE]}
          >
            <Input placeholder="ops@vendor.com" />
          </Form.Item>
          <Form.Item
            name="bu"
            label="Business Unit"
            extra="The business unit that owns this vendor. Users with roles in that business unit can view and manage it and its transfers."
            rules={[{ required: true, message: 'Business Unit is required' }]}
          >
            <Select placeholder="Select Business Unit" options={buOptions} />
          </Form.Item>
          {assignTag && (
            <Form.Item
              name="tag"
              label="Tag"
              extra="Optional. Organizational only. It does not change who can access this vendor."
            >
              <Select
                allowClear
                placeholder="No tag"
                options={tagChoices.map((tag) => ({ value: tag, label: tag }))}
              />
            </Form.Item>
          )}
          <Form.Item
            name="outbound_bucket_suffix"
            label="Vendor Bucket"
            extra={
              <>
                Enter only the vendor-specific name (e.g. &apos;alta&apos;). The &apos;{BUCKET_PREFIX}&apos; prefix is added automatically.
                <br />
                The bucket is provisioned with Incoming/, Outgoing/, and Processed/ folders.
              </>
            }
            rules={[{ required: true, validator: validateBucketSuffix }]}
          >
            <Input addonBefore={BUCKET_PREFIX} placeholder="e.g. alta" />
          </Form.Item>
          <Form.Item
            name="outbound_prefix"
            label="Outbound Prefix"
            extra="Optional. Defaults to 'Outgoing'. Not currently used to construct the delivery path."
          >
            <Input placeholder="Outgoing" />
          </Form.Item>
          <Title level={4} style={{ marginTop: 24 }}>Source</Title>
          <Form.Item
            name="source_endpoint_id"
            label="Inbound delivery endpoint"
            extra={
              endpointsUnavailable
                ? 'Inbound files are delivered to this endpoint. Source shares could not be loaded. This vendor will use the default share.'
                : 'Inbound files are delivered to this endpoint.'
            }
          >
            <Select
              allowClear
              placeholder="Select an endpoint"
              loading={endpointsLoading}
              disabled={endpointsUnavailable}
              options={endpointOptions}
            />
          </Form.Item>
          <SourceRoutesEditor
            options={endpointOptions}
            loading={endpointsLoading}
            unavailable={endpointsUnavailable}
            disabled={submitting}
            followVendorId
          />
          <Form.Item name="send_email_to_vendor" valuePropName="checked">
            <Checkbox>
              Send onboarding email to vendor email (uncheck to suppress the email while testing)
            </Checkbox>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={submitting}>
                Create Vendor
              </Button>
              <Button onClick={() => navigate(listPath)}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default VendorCreatePage;
